import { useCallback, useEffect, useRef, useState } from 'react';
import { NotificationItem, notificationsClient } from '../client/notificationsClient';
import { useAuth } from '../contexts/AuthContext';

/** One panel's worth of recent notifications. */
const LIST_ROWS = 20;
const POLL_MS = 30_000;
/** A focus refetch within this window of the last load is skipped. */
const FOCUS_REFETCH_MIN_INTERVAL_MS = 10_000;

export interface NotificationsState {
    items: NotificationItem[];
    unreadCount: number;
    /** True only until the FIRST load answers — polls never flicker it. */
    loading: boolean;
    markRead: (id: string) => void;
    markAllRead: () => void;
    refetch: () => void;
}

/**
 * The bell's data: recent notifications + unread count, polled every 30s
 * while the tab is visible and refetched on window focus. Mark-read is
 * optimistic — the badge drops immediately; a failed call re-syncs from the
 * server instead of surfacing. Resilient by design: a failed poll never
 * throws into the UI, it just leaves the last good state standing.
 */
export const useNotifications = (): NotificationsState => {
    const { backendUser } = useAuth();
    const userId = backendUser?.userId;

    const [items, setItems] = useState<NotificationItem[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const lastLoadedAt = useRef(0);
    // Guards against a slow in-flight response overwriting a newer one.
    const requestSeq = useRef(0);

    const load = useCallback(async () => {
        if (!userId) return;
        const seq = ++requestSeq.current;
        try {
            const data = await notificationsClient.list({ limit: LIST_ROWS });
            if (seq !== requestSeq.current) return;
            setItems(data.items);
            setUnreadCount(data.unreadCount);
            lastLoadedAt.current = Date.now();
        } catch {
            // Best-effort: keep the last good state; the next tick covers it.
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    }, [userId]);

    // Poll while visible; a hidden tab holds no timer and issues no request.
    useEffect(() => {
        if (!userId) {
            setItems([]);
            setUnreadCount(0);
            return undefined;
        }
        let interval: number | undefined;
        const stop = () => {
            if (interval === undefined) return;
            window.clearInterval(interval);
            interval = undefined;
        };
        const start = () => {
            load();
            if (interval === undefined) interval = window.setInterval(load, POLL_MS);
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') start();
            else stop();
        };
        const onFocus = () => {
            if (Date.now() - lastLoadedAt.current < FOCUS_REFETCH_MIN_INTERVAL_MS) return;
            load();
        };

        onVisibilityChange();
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('focus', onFocus);
        return () => {
            stop();
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('focus', onFocus);
        };
    }, [userId, load]);

    const markRead = useCallback(
        (id: string) => {
            const target = items.find((n) => n.id === id);
            if (!target || target.readAt) return;
            // Optimistic: the row and badge update now; the server call follows.
            const readAt = new Date().toISOString();
            setItems((prev) => prev.map((n) => (n.id === id ? { ...n, readAt } : n)));
            setUnreadCount((prev) => Math.max(0, prev - 1));
            notificationsClient.markRead(id).catch(() => load());
        },
        [items, load]
    );

    const markAllRead = useCallback(() => {
        if (unreadCount === 0) return;
        const readAt = new Date().toISOString();
        setItems((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt })));
        setUnreadCount(0);
        notificationsClient.markAllRead().catch(() => load());
    }, [unreadCount, load]);

    return { items, unreadCount, loading, markRead, markAllRead, refetch: load };
};

export default useNotifications;
